import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
} from "@material-ui/core";
import {
  append,
  mergeDeepRight,
  mergeRight,
  propEq,
  reject,
  remove,
} from "ramda";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import YesNoCard from "../../components/YesNoCard";
import EditIcon from "@material-ui/icons/Edit";
import ProjectOptInDialog from "../edit/optin/ProjectOptInDialog";
import ConfirmButton from "../../components/ConfirmButton";
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import NewOptInDialog from "../edit/optin/NewOptInDialog";
import { caseInsensitiveSort } from "../../util/sorts";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  cell: {
    minWidth: 100,
  },
}));

const ProjectOptInsCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();

  const [selectedOptIn, setSelectedOptIn] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newOptInDialog, setNewOptInDialog] = useState(false);

  const handleAddClick = useCallback(() => {
    setSelectedOptIn(null);
    setDialogOpen(true);
  }, []);

  const handleEditClick = useCallback(optInId => {
    setSelectedOptIn(optInId);
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setSelectedOptIn(null);
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setDialogOpen(false);
      setSelectedOptIn(null);
      console.log(data);
      const existingOptIns = reject(
        propEq("optInId", data.optInId),
        project.optIns,
      );
      console.log(existingOptIns);
      const update = mergeRight(project, {
        optIns: append(data, existingOptIns),
      });
      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async idx => {
      const update = mergeDeepRight(project, {
        optIns: remove(idx, 1, project.optIns),
      });
      onUpdate(update);
    },
    [project, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Opt-Ins"
        action={
          <Grid container style={{ paddingTop: 10 }}>
            <Button
              color="primary"
              onClick={handleAddClick}
              startIcon={<FontAwesomeIcon icon={faPlus} />}
            >
              Add
            </Button>
            <Button
              color="primary"
              onClick={() => setNewOptInDialog(true)}
              startIcon={<FontAwesomeIcon icon={faPlus} />}
            >
              Create
            </Button>
          </Grid>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project.optIns) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Opt-Ins set up for this Project.
          </Box>
        )}
        {notBlank(project.optIns) && (
          <TableContainer style={{ maxHeight: 350, overflowY: "auto" }}>
            <Table stickyHeader aria-label="Opt-Ins">
              <TableHead>
                <TableRow>
                  <TableCell
                    key="opt-in-name"
                    align="left"
                    className={classes.cell}
                  >
                    Name
                  </TableCell>

                  <TableCell
                    key="pre-submission"
                    align="center"
                    className={classes.cell}
                  >
                    Pre-submission
                  </TableCell>

                  <TableCell
                    key="required"
                    align="center"
                    className={classes.cell}
                  >
                    Required
                  </TableCell>

                  <TableCell
                    key="actions"
                    align="center"
                    style={{ width: 100 }}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {mapIndexed(
                  (optin, idx) => (
                    <TableRow hover tabIndex={-1} key={optin.optInId}>
                      <TableCell
                        key={"opt-in-name:" + optin.optInId}
                        align="left"
                        className={classes.cell}
                      >
                        {optin.optInName}
                      </TableCell>

                      <TableCell
                        key={"pre-submission:" + optin.optInId}
                        align="center"
                        className={classes.cell}
                      >
                        <YesNoCard
                          value={optin.preSubmission}
                          showNoIcon={false}
                        />
                      </TableCell>

                      <TableCell
                        key={"required:" + optin.optInId}
                        align="center"
                        className={classes.cell}
                      >
                        <YesNoCard value={optin.required} showNoIcon={false} />
                      </TableCell>

                      <TableCell
                        key={"actions:" + optin.optInId}
                        align="center"
                        style={{ width: 100 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          pl={4}
                        >
                          <Tooltip title="Edit">
                            <IconButton
                              classes={{ label: classes.icon }}
                              onClick={() => handleEditClick(optin)}
                            >
                              <EditIcon color="primary" fontSize="large" />
                            </IconButton>
                          </Tooltip>
                          <ConfirmButton
                            handleDelete={() => handleDeleteClick(idx)}
                            tooltipTitle="Remove From Project"
                            dialogTitle="Remove Opt In"
                            dialogBody="Are you sure you want to remove this opt in?"
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ),
                  caseInsensitiveSort("optInName")(project.optIns),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {dialogOpen && (
        <ProjectOptInDialog
          open={dialogOpen}
          project={project}
          optIn={selectedOptIn}
          onCancel={handleCancel}
          onApply={handleDialogApply}
        />
      )}

      {newOptInDialog && (
        <NewOptInDialog
          open={newOptInDialog}
          onClose={() => setNewOptInDialog(false)}
        />
      )}
    </Card>
  );
};

export default ProjectOptInsCard;
